const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export type CommunicationPreference = 'Email' | 'Teams' | 'Sms';

export interface Employee {
  id: string;
  name: string;
  email: string;
  personalEmail: string | null;
  teamLabel: string;
  department: string | null;
  manager: string | null;
  communicationPreference: CommunicationPreference;
  phoneNumber: string | null;
  entraObjectId: string | null;
  isActive: boolean;
}

export interface CreateEmployeeRequest {
  name: string;
  email: string;
  personalEmail?: string;
  teamLabel: string;
  department?: string;
  manager?: string;
  communicationPreference: CommunicationPreference;
  phoneNumber?: string;
  entraObjectId?: string;
}

export interface ImportResult {
  created: number;
  updated: number;
  warnings: string[];
}

export interface EntraSyncResult {
  created: number;
  updated: number;
  deactivated: number;
  warnings: string[];
}

export interface ChannelCountDto {
  channel: CommunicationPreference;
  count: number;
}

export interface NoContactEmployeeDto {
  id: string;
  name: string;
  email: string;
}

export interface DeliveryPreviewResult {
  totalEmployees: number;
  byChannel: ChannelCountDto[];
  noContact: NoContactEmployeeDto[];
}

export interface ReminderInput {
  scheduledAt: string; // ISO 8601
}

// ── Auth helper ───────────────────────────────────────────────────────────────

async function authFetch(url: string, accessToken: string, init?: RequestInit) {
  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res;
}

// ── Employee CRUD ─────────────────────────────────────────────────────────────

export async function fetchEmployees(
  accessToken: string,
  params?: { team?: string; activeOnly?: boolean }
): Promise<Employee[]> {
  const qs = new URLSearchParams();
  if (params?.team) qs.set('team', params.team);
  if (params?.activeOnly === false) qs.set('activeOnly', 'false');
  const res = await authFetch(`/api/employees?${qs}`, accessToken);
  return res.json();
}

export async function createEmployee(
  accessToken: string,
  body: CreateEmployeeRequest
): Promise<Employee> {
  const res = await authFetch('/api/employees', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function updateEmployee(
  accessToken: string,
  id: string,
  body: Partial<Employee>
): Promise<Employee> {
  const res = await authFetch(`/api/employees/${id}`, accessToken, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

export async function importEmployeesCsv(
  accessToken: string,
  file: File
): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await authFetch('/api/employees/import', accessToken, {
    method: 'POST',
    body: form,
  });
  return res.json();
}

/** Syncs active employees from Entra ID (admin action). */
export async function syncEmployeesFromEntra(accessToken: string): Promise<EntraSyncResult> {
  const res = await authFetch('/api/employees/sync-entra', accessToken, { method: 'POST' });
  return res.json();
}

/** Permanently deletes the given employees (admin action). */
export async function deleteEmployees(accessToken: string, ids: string[]): Promise<{ deleted: number }> {
  const res = await authFetch('/api/employees/delete', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  return res.json();
}

/** Returns the URL for the template CSV (authenticated via bearer — use as anchor href). */
export function getEmployeeTemplateUrl(): string {
  return `${BASE}/api/employees/template`;
}

// ── Delivery preview ──────────────────────────────────────────────────────────

export async function fetchDeliveryPreview(
  accessToken: string,
  surveyId: string,
  employeeIds: string[],
  channelOverride?: CommunicationPreference
): Promise<DeliveryPreviewResult> {
  const qs = new URLSearchParams();
  employeeIds.forEach(id => qs.append('employeeIds', id));
  if (channelOverride) qs.set('channelOverride', channelOverride);
  const res = await authFetch(
    `/api/surveys/${surveyId}/delivery-preview?${qs}`,
    accessToken
  );
  return res.json();
}

// ── Survey delivery ───────────────────────────────────────────────────────────

export async function deliverSurvey(
  accessToken: string,
  surveyId: string,
  employeeIds: string[],
  expiresAt: string,
  reminders: ReminderInput[],
  channelOverride?: CommunicationPreference,
  invitationMessage?: string,
  landingPageMessage?: string
): Promise<{ succeeded: number; failed: number; failures: { email: string; reason: string }[] }> {
  const res = await authFetch(`/api/surveys/${surveyId}/deliver`, accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      employeeIds,
      expiresAt,
      reminders,
      channelOverride,
      invitationMessage,
      landingPageMessage,
    }),
  });
  return res.json();
}
